import React, { useState, FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { buildLocStoreKeys } from 'store/types';

interface IContextState {
  setLanguage: (language: string) => void;
  language: string | null;
}

export const LanguageContext = React.createContext<IContextState>({
  setLanguage: () => {},
  language: null,
});

interface IProps {
  children: React.ReactNode;
}

export const LanguageProvider: FC<IProps> = ({ children }: IProps) => {
  const LocStoreKeys = buildLocStoreKeys();

  const [language, setLanguage] = useState<string | null>(window.localStorage.getItem(LocStoreKeys.LANGUAGE) || 'bg');
  const { i18n, t } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage(language || 'bg');
    document.title = t('common.pageTitle');
  }, []);

  /**
   * Subscribe to local storage updates to handle the case
   * if app is open in more than one tab
   * and Language has been updated in any of the tabs
   */
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === LocStoreKeys.LANGUAGE) {
        setLanguage(e.newValue);
        i18n.changeLanguage(e.newValue || 'bg');
        document.title = t('common.pageTitle');
      }
    };
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('storage', handler);
    };
  });

  const setLanguageStateAndStore = (lang: string) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    document.title = t('common.pageTitle');
    if (lang) return window.localStorage.setItem(LocStoreKeys.LANGUAGE, lang);

    return window.localStorage.removeItem(LocStoreKeys.LANGUAGE);
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage: setLanguageStateAndStore,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export function useLanguageContext(): IContextState {
  return React.useContext(LanguageContext);
}
